/**
 * Notification adapter selector.
 *
 * Picks the right platform implementation once and memoises it. The Tauri
 * adapter (and its plugin JS) is only imported when actually running inside a
 * Tauri WebView, so the plain web/PWA bundle never pulls it in.
 */

import { isTauriRuntime } from "@/lib/platform";

import type { NotificationAdapter } from "./types";
import { WebNotificationAdapter } from "./web-adapter";

let adapterPromise: Promise<NotificationAdapter> | null = null;

async function createAdapter(): Promise<NotificationAdapter> {
  if (isTauriRuntime()) {
    const { TauriNotificationAdapter } = await import("./tauri-adapter");
    return new TauriNotificationAdapter();
  }
  return new WebNotificationAdapter();
}

async function getNotificationAdapter(): Promise<NotificationAdapter> {
  adapterPromise ??= createAdapter();
  return adapterPromise;
}

/**
 * Cancels every OS-scheduled reminder. Call when local data is wiped (logout /
 * account switch) so reminders for deleted todos never fire. No-op on the web.
 */
export async function clearAllScheduledReminders(): Promise<void> {
  const adapter = await getNotificationAdapter();
  await adapter.cancelAll();
}
